import { IncomingMessage } from 'http';
import { useInfiniteQuery } from '@tanstack/react-query';
import flatten from 'lodash-es/flatten';
import { useMemo } from 'react';
import { getFbkRecentVisitProductList } from 'api2';
import { GetFbkRecentVisitProductList } from 'api2/types';
import { ProductCardProps } from 'components/common/ProductCard';
import { convertIsFastDelivery } from 'util/product_util';

export type RecentVisitProductList = GetFbkRecentVisitProductList['fbk_recent_visit_product_list'];
export type ProductItem = NonNullable<RecentVisitProductList['item_list']>[number];

export const RECENT_VISIT_PRODUCT_LIST_QUERY_KEY = ['getRecentVisitProductList'];

const PAGE_PER_COUNT = 100;

function uiItemToCardProps(item: ProductItem) {
  const product_card: ProductCardProps = {
    ...item,
    catalog_product_id: item.catalog_product_id,
    is_new: item.is_new ?? false,
    shop: { id: item.shop_id, name: item.shop_name ?? '' },
    url: item.product_url ?? '',
    zpay: item.zpay ?? null,
    image_url: item.image_url ?? '',
    webp_image_url: item.webp_image_url ?? '',
    title: item.title ?? '',
    discount_rate: item.discount_rate ?? null,
    price: item.price ?? null,
    free_shipping: item.free_shipping ?? null,
    shop_product_no: item.shop_product_no ?? null,
    is_fast_delivery: convertIsFastDelivery(item.badge_list),
  };
  return product_card;
}

export const fetchRecentProductList = async (req?: IncomingMessage) => {
  const context_request = req ? { context: req, show_alert: false } : { show_alert: false };

  const { data } = await getFbkRecentVisitProductList({ limit_count: PAGE_PER_COUNT }, context_request);

  return data.fbk_recent_visit_product_list;
};

export const useRecentProductList = () => {
  const {
    data,
    isInitialLoading: is_recent_product_loading,
    ...rest
  } = useInfiniteQuery(RECENT_VISIT_PRODUCT_LIST_QUERY_KEY, ({ pageParam }) => fetchRecentProductList(pageParam), {
    getNextPageParam: (last_page) => last_page.end_cursor,
    retryOnMount: true,
  });

  const total_count = useMemo(() => data?.pages[0].total_count || 0, [data]);

  const recent_product_list = useMemo(() => {
    // @ts-ignore
    return flatten(data?.pages.map((value) => value.item_list)).map(uiItemToCardProps);
  }, [data]);

  return {
    recent_product_list,
    total_count,
    is_recent_product_loading,
    ...rest,
  };
};
